import { cookieConsentStoreInstance } from '../store/cookie-consent.store'

const GTM_ID = 'GTM-KRNV5DR7'
const GA4_ID = 'G-94TDNT1QS6'

function loadScriptWithConsent(
  src: string,
  consentType: 'analytics' | 'marketing' | 'preferences',
): Promise<void> {
  return new Promise((resolve, reject) => {
    const hasConsent = consentType === 'analytics'
      ? cookieConsentStoreInstance.hasAnalyticsConsent
      : consentType === 'marketing'
        ? cookieConsentStoreInstance.hasMarketingConsent
        : cookieConsentStoreInstance.hasPreferencesConsent

    if (!hasConsent) {
      reject(new Error(`Consent not given for ${consentType} cookies`))
      
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

export function initializeGTM(): void {
  if (!cookieConsentStoreInstance.hasAnalyticsConsent) {
    return
  }

  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
    document.head.appendChild(script)

    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.insertBefore(noscript, document.body.firstChild)
  } catch (error) {
    console.error('[GTM] Failed to initialize:', error)
  }
}

export async function initializeGA4(): Promise<void> {
  if (!cookieConsentStoreInstance.hasAnalyticsConsent) {
    return
  }

  try {
    await loadScriptWithConsent(
      `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`,
      'analytics',
    )

    window.dataLayer = window.dataLayer || []

    window.gtag = (...args: any[]) => {
      window.dataLayer.push(args)
    }

    window.gtag('js', new Date())
    window.gtag('config', GA4_ID)
  } catch (error) {
    console.error('[GA4] Failed to initialize:', error)
  }
}

export function initializeAnalytics(): void {
  if (cookieConsentStoreInstance.hasAnalyticsConsent) {
    initializeGTM()
    initializeGA4()
  }
}

export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  if (!cookieConsentStoreInstance.hasAnalyticsConsent) {
    return
  }

  if (window.gtag) {
    window.gtag('event', eventName, eventParams)
  }
}

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!cookieConsentStoreInstance.hasAnalyticsConsent) {
    return
  }

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    })
  }
}

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}
